import { Icon } from '@natera/material/lib/icon'
import { useEffect, useState } from 'react'

import type { apiClient } from '~/shared/api/data-ingestion-api'
import { cn } from '~/shared/lib/cn'
import { formatCurrency } from '~/shared/lib/currency'
import { formatDateDefault } from '~/shared/lib/datetime'
import { InfoTip, LocalIcon, RadioButton, Text } from '~/shared/ui'
import { NO_DATA_PLACEHOLDER } from '../../config'
import {
  CardDivider,
  ContradictionChip,
  FieldRow,
  StatusIcon,
  formatCriterion,
  getFieldStatus,
  resolveFieldStatus,
  type FieldStatus,
} from '../pa-matches/pa-card-shared'
import { ConfidenceBadge } from '../patients/confidence-badge'

export function ClaimMatchCard({
  record,
  isSystemSelected,
  isSelectable = false,
  isSelected = false,
  onSelect,
  onHide,
  canHide = false,
  showContradictions = false,
  hoveredField = null,
  documentFields,
  connectUrl,
  isUserCreated = false,
  isMatchingV2 = false,
}: {
  record: apiClient.PatientRecordResponse
  isSystemSelected: boolean
  isSelectable?: boolean
  isSelected?: boolean
  onSelect?: () => void
  onHide?: () => void
  canHide?: boolean
  showContradictions?: boolean
  hoveredField?: string | null
  documentFields?: Record<string, string | undefined>
  connectUrl?: string
  isUserCreated?: boolean
  isMatchingV2?: boolean
}) {
  const similarity = record.similarity
  const matchedOn = record.matched_on ?? []
  const contradictedOn = record.contradicted_on ?? []
  const [isContradictedExpanded, setIsContradictedExpanded] = useState(showContradictions)

  useEffect(() => {
    setIsContradictedExpanded(showContradictions)
  }, [showContradictions])

  const fieldMap: Record<
    string,
    { record?: string | null; extracted?: string | null; isDate?: boolean }
  > = {
    date_of_birth: {
      record: record.date_of_birth,
      extracted: documentFields?.member_dob,
      isDate: true,
    },
    payor_name: { record: record.payorname, extracted: documentFields?.insurance_company },
    test_name: { record: record.test_name, extracted: documentFields?.test_name },
    date_of_service: {
      record: record.lims_date_of_service,
      extracted: documentFields?.service_date_from,
      isDate: true,
    },
    total_billed: {
      record: record.billprice
        ? String(parseFloat(record.billprice.replace(/[^0-9.-]/g, '')))
        : null,
      extracted: documentFields?.total_amount
        ? String(parseFloat(documentFields.total_amount.replace(/[^0-9.-]/g, '')))
        : null,
    },
    appeal_date: {
      record: record.appeal_date,
      extracted: documentFields?.appeal_date_received,
      isDate: true,
    },
    member_id: {
      record: record.policy_number?.replace(/^\[|\]$/g, ''),
      extracted: documentFields?.member_id,
    },
    visit_id: { record: record.visitfid, extracted: documentFields?.patient_account_number },
    claim_id: {
      record: record.claim_id,
      extracted: documentFields?.natera_claim_id ?? documentFields?.claim_number,
    },
    cpt_codes: { record: record.allproccodes_claim, extracted: documentFields?.cpt_code },
  }

  function status(fieldKey: string) {
    if (!isMatchingV2) return getFieldStatus(fieldKey, matchedOn, contradictedOn)
    const f = fieldMap[fieldKey]
    return resolveFieldStatus(fieldKey, matchedOn, contradictedOn, f?.record, f?.extracted, {
      isDate: f?.isDate,
    })
  }

  const allContradictions = isMatchingV2
    ? Object.keys(fieldMap).filter((key) => status(key) === 'contradiction')
    : contradictedOn

  return (
    <div
      className={cn(
        'bg-surface-lowest flex h-full flex-col overflow-hidden rounded-lg border whitespace-nowrap',
        {
          'border-chip-status-success-content border-2': isSystemSelected,
          'border-primary-main border-2': isSelectable && isSelected,
          'border-divider': !isSystemSelected && !(isSelectable && isSelected),
        },
      )}
    >
      {isSelectable ? (
        <div className="border-divider min-h-[52px] border-b">
          <button
            className="bg-surface-lowest relative flex h-full w-full cursor-pointer items-center justify-between border-none px-4 py-3"
            type="button"
            onClick={onSelect}
          >
            <RadioButton
              label={isMatchingV2 && isUserCreated ? 'Added by user' : 'Match'}
              withinButton
              checked={isSelected}
              onChange={() => onSelect?.()}
            />
          </button>
        </div>
      ) : (
        <div
          className={cn('flex min-h-[52px] items-center justify-between border-b px-4 py-3', {
            'border-chip-status-success-content/30 bg-chip-status-success-content/5':
              isSystemSelected,
            'border-divider': !isSystemSelected,
          })}
        >
          <Text
            className={cn({
              'text-chip-status-success-content': isSystemSelected,
              'text-text-secondary': !isSystemSelected,
            })}
            appearance="CAPTION"
          >
            {isSystemSelected ? 'System selected' : 'Claim'}
          </Text>
          {similarity != null && (
            <ConfidenceBadge
              confidencePercentage={Math.round(similarity * 100)}
              label={similarity.toFixed(2)}
            />
          )}
        </div>
      )}

      <div className="px-4 pt-2">
        <Text appearance="OVERLINE">Natera Patient Data</Text>
      </div>

      <div className="flex flex-col gap-2 px-4 py-3">
        <Text
          appearance="SUBTITLE"
          placeholder={NO_DATA_PLACEHOLDER}
          truncated
          withTooltip
          copyable
        >
          {record.patient_first_name} {record.patient_last_name}
        </Text>
        <FieldRow
          label="Birth Date"
          value={record.date_of_birth ? formatDateDefault(record.date_of_birth) : null}
          status={status('date_of_birth')}
          hoveredField={hoveredField}
          hoveredFieldKey="birth_date"
          extractedValue={
            documentFields?.member_dob ? formatDateDefault(documentFields.member_dob) : undefined
          }
        />
        <FieldRow
          label="Payor Name"
          value={record.payorname}
          status={status('payor_name')}
          hoveredField={hoveredField}
          hoveredFieldKey="insurance_company"
          extractedValue={documentFields?.insurance_company}
        />
        <FieldRow
          label="Test Name"
          value={record.test_name}
          status={status('test_name')}
          hoveredField={hoveredField}
          hoveredFieldKey="test_name"
          extractedValue={documentFields?.test_name}
        />
        <FieldRow
          label="Date of Service"
          value={
            record.lims_date_of_service ? formatDateDefault(record.lims_date_of_service) : null
          }
          status={status('date_of_service')}
          hoveredField={hoveredField}
          hoveredFieldKey="date_of_service"
          extractedValue={
            documentFields?.service_date_from
              ? formatDateDefault(documentFields.service_date_from)
              : undefined
          }
        />
        <FieldRow
          label="Total Billed"
          value={formatCurrency(record.billprice)}
          status={status('total_billed')}
          hoveredField={hoveredField}
          hoveredFieldKey="billprice"
          extractedValue={documentFields?.total_amount}
        />
        <FieldRow
          label="Appeal Date"
          value={record.appeal_date ? formatDateDefault(record.appeal_date) : null}
          status={status('appeal_date')}
          hoveredField={hoveredField}
          hoveredFieldKey="appeal_date"
          extractedValue={
            documentFields?.appeal_date_received
              ? formatDateDefault(documentFields.appeal_date_received)
              : undefined
          }
        />
      </div>

      <CardDivider />

      <div className="flex flex-col gap-3 px-4 py-3">
        <FieldRow
          label="Member ID"
          value={record.policy_number?.replace(/^\[|\]$/g, '')}
          status={status('member_id')}
          hoveredField={hoveredField}
          hoveredFieldKey="member_id"
          extractedValue={documentFields?.member_id}
        />
        <LimsCaseIdField
          value={record.lims_casefile_id}
          status="not_applicable"
          hoveredField={hoveredField}
          extractedValue={documentFields?.case_number}
        />
        <FieldRow
          label="Visit ID"
          value={record.visitfid}
          status={status('visit_id')}
          hoveredField={hoveredField}
          hoveredFieldKey="patient_account_number"
          extractedValue={documentFields?.patient_account_number}
        />
        <FieldRow
          label="Payor Claim ID"
          value={record.claim_id}
          status={status('claim_id')}
          hoveredField={hoveredField}
          hoveredFieldKey="claim_number"
          extractedValue={documentFields?.natera_claim_id ?? documentFields?.claim_number}
        />
        <FieldRow
          label="CPT Code(s)"
          value={record.allproccodes_claim?.replace(/;/g, ', ')}
          status={status('cpt_codes')}
          hoveredField={hoveredField}
          hoveredFieldKey="cpt_code"
          extractedValue={documentFields?.cpt_code}
        />
      </div>

      <CardDivider />

      <div className="flex flex-col">
        <button
          className="flex w-full cursor-pointer items-center gap-2 border-none bg-transparent px-4 pt-4 text-left"
          type="button"
          onClick={() => setIsContradictedExpanded((v) => !v)}
        >
          <Text
            className="flex-1"
            appearance="OVERLINE"
          >
            Contradicted on{isContradictedExpanded ? '' : ` (${allContradictions.length})`}
          </Text>
          <Icon
            className="text-[20px]"
            children={isContradictedExpanded ? 'expand_less' : 'expand_more'}
          />
        </button>
        {isContradictedExpanded && (
          <div className="flex flex-wrap gap-1 px-4 py-3 whitespace-normal">
            {allContradictions.length > 0 ? (
              allContradictions.map((criterion) => (
                <ContradictionChip key={criterion}>{formatCriterion(criterion)}</ContradictionChip>
              ))
            ) : (
              <Text
                className="text-text-secondary"
                appearance="BODY2"
              >
                None
              </Text>
            )}
          </div>
        )}
      </div>

      <div className="grow" />
      <div className="border-divider flex items-center justify-between border-t p-4">
        {connectUrl ? (
          <a
            className="text-text-link flex cursor-pointer items-center gap-1 border-none bg-transparent p-0 no-underline"
            target="_blank"
            href={connectUrl}
          >
            <Text appearance="BODY2">More</Text>
            <LocalIcon
              name="openInNew"
              size="18"
            />
          </a>
        ) : (
          <span className="text-text-link flex items-center gap-1">
            <Text appearance="BODY2">More</Text>
            <LocalIcon
              name="openInNew"
              size="18"
            />
          </span>
        )}
        <button
          className={cn(
            'flex cursor-pointer items-center gap-1 border-none bg-transparent p-0',
            canHide ? 'text-text-secondary' : 'text-text-disabled cursor-not-allowed',
          )}
          type="button"
          disabled={!canHide}
          onClick={onHide}
        >
          <Text appearance="BODY2">Hide</Text>
          <Icon
            className="text-[18px]"
            children="visibility_off"
          />
        </button>
      </div>
    </div>
  )
}

function LimsCaseIdField({
  value,
  status,
  hoveredField,
  extractedValue,
}: {
  value: string | null | undefined
  status: FieldStatus
  hoveredField: string | null | undefined
  extractedValue: string | undefined
}) {
  const hasValue = value != null && value !== ''
  const isHighlighted = hoveredField != null && hoveredField === 'case_number'
  const hoverMatches =
    isHighlighted &&
    hasValue &&
    !!extractedValue &&
    value?.trim().toLowerCase() === extractedValue?.trim().toLowerCase()
  return (
    <div
      className={cn('flex h-5 items-center gap-1 px-0.5 transition-colors', {
        'bg-secondary-focused': isHighlighted && hoverMatches,
        'bg-error-focused': isHighlighted && !hoverMatches,
      })}
    >
      <div className="flex min-w-0 flex-1 items-center justify-between gap-2 whitespace-nowrap">
        <div className="flex shrink-0 items-center gap-0.5">
          <Text
            className="text-text-secondary"
            appearance="BODY2"
          >
            LIMS Case ID
          </Text>
          <InfoTip iconSize="16">
            <Text
              className="text-text-secondary max-w-[265px] pr-6"
              appearance="CAPTION"
            >
              AKA: Case ID, LIMS Case ID.
              <br />A unique identifier assigned by Natera Sales Order System (SOS) to a test that
              ties back to the physical sample we receive from a patient.
            </Text>
          </InfoTip>
        </div>
        {hasValue && (
          <Text
            className="min-w-0 text-right"
            appearance="BODY2"
            truncated
            withTooltip
            copyable
          >
            {value}
          </Text>
        )}
      </div>
      <StatusIcon status={status} />
    </div>
  )
}
